/**
 * @fileoverview Geographic region management actions for Mexican locations
 * @module RegionActions
 */

import apiClient from "../utils/apiClient";
import useSWR from "swr";
import queryString from "query-string";
/**
 * @typedef {Object} SWRObject
 * @property {object} data - The data returned by the swr call
 * @property {boolean} isLoading - State of the swr call
 */

/**
 * Hook to obtain the states of the Mexican republic
 * @function
 * @name useStates
 * @memberof module:RegionActions
 * @returns {SWRObject} SWR Object containing states data
 */

export const useStates = () => {
  const fetcher = (url) => apiClient.get(url).then((res) => res.data.results);
  const { data, error } = useSWR("/locations/state/", fetcher);
  return { data, isLoading: !error && !data, error };
};
/**
 * @typedef {Object} SWRObject
 * @property {object} data - The data returned by the swr call
 * @property {boolean} isLoading - State of the swr call
 */

/**
 * Hook to obtain municipalities within a state
 * @function
 * @name useMunicipalities
 * @memberof module:RegionActions
 * @param {Object} params - Parameters including state ID
 * @returns {SWRObject} SWR Object containing municipalities data
 */

export const useMunicipalities = (params) => {
  const fetcher = (url) => apiClient.get(url).then((res) => res.data.results);
  const { data, error } = useSWR(
    `/locations/municipality/?${queryString.stringify(params)}`,
    fetcher,
  );

  return { data, isLoading: !error && !data, error };
};

/**
 * @typedef {Object} SWRObject
 * @property {object} data - The data returned by the swr call
 * @property {boolean} isLoading - State of the swr call
 */

/**
 * Hook to obtain neighborhoods within a municipality
 * @function
 * @name useNeighborhoods
 * @memberof module:RegionActions
 * @param {Object} params - Parameters including municipality ID
 * @returns {SWRObject} SWR Object containing neighborhoods data
 */

export const useNeighborhoods = (params) => {
  const fetcher = (url) => apiClient.get(url).then((res) => res.data);
  const { data, error } = useSWR(
    params?.municipality ? `/locations/neighborhood/?${queryString.stringify(params)}` : null,
    fetcher,
  );
  return { data, isLoading: !error && !data, error };
};
